/**
 * @module  experience/shared-browse
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

import { Component, OnInit, Input, OnDestroy, Output, EventEmitter } from '@angular/core';

// Interfaces
import { BrowsePlanConfig, Tooltip } from '@core/aem/interfaces/shop/browse-plan.interface';
// Constants
import { SHARED_BROWSE_CONSTANTS } from '@experience/shop/shared-browse/constants/shared-browse.constants';

// Services
import { Subject } from 'rxjs';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { PlanOption, PlansDuration } from '@support/api/proxies/responses/api-2117.interface';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { FeatureToggleUtilService } from '@support/shared/services/feature-toggle-util.service';

@Component({
  selector: 'tmo-plan-sub-title',
  templateUrl: './plan-sub-title.component.html',
})
export class PlanSubTitleComponent implements OnInit, OnDestroy {
  /**
   * Configurable AEM data passed from parent component
   */
  @Input() public set browsePlanAemContent(aemContent: BrowsePlanConfig) {
    this.aemContent = aemContent;
    const tooltipArray = this.objectService.get(this.aemContent, 'unlimitedPlanTooltip', '');
    this.tooltipList = tooltipArray ? JSON.parse(tooltipArray) : [];
  }
  /**
   * stores to check plan Id
   */
  @Input() public selectedFamilyId: string;
  /**
   * stores plan options
   */
  @Input() public set planOptions(plans: PlanOption[]) {
    this.plans = plans;
    this.groupPlansByDurtion(this.plans);
    this.setDefaultMonth();
    this.handleRadioClickEvent(this.selectedDuration);
  }
  /**
   *  Flag to hide plans heading in case of PS page
   */
  @Input() public hidePlansHeader: boolean;

  /**
   *  Event Emitted on Radio cta toggle
   */
  @Output() public onMonthSelection: EventEmitter<string>;
  /**
   *  Event Emitting plans filtered by Duration
   */
  @Output() public plansFiltertedByDuration: EventEmitter<PlanOption[]>;
  /**
   * Subject to manage subscriptions
   */
  private ngUnsubscribe: Subject<void>;
  /**
   * variable to hold array of objects for tooltip content for tmoUnlimitedRatePlans
   */
  public tooltipList: Tooltip[];
  /**
   * variable to check if plan is tmoUnlimitedRatePlans
   */
  public tmoUnlimitedRatePlans: boolean;
  /**
   * variable to check if plan is connectByTmobile
   */
  public connectByTmobile: boolean;
  /**
   * variable to check if plan is unltMintPlans
   */
  public unltMintPlans: boolean;
  public plans: PlanOption[];
  /**
   *  stores durations of plans
   */
  public plansDuration: PlansDuration[];
  /**
   *  stores value of default month selected.
   */
  public defaultMonthSelected: string;
  /**
   *  stores value of selected duration
   */
  public selectedDuration: string;
  /**
   *  stores value of selected month
   */
  public plansByDuration: PlanOption[];
  /**
   *  flag to hide/show radio buttons incase of connect plans
   */
  public aemContent: BrowsePlanConfig;
  /**
   *  flag to hide/show connect theme
   */
  public showConnect: boolean;
  public displayConnectPlansRadioButton: boolean;
  constructor(
    private objectService: ObjectService,
    private stateStoreUtilService: StateStoreUtilsService,
    private commonUtilService: CommonUtilService,
    private featureToggleUtilService: FeatureToggleUtilService
  ) {
    this.ngUnsubscribe = new Subject<void>();
    this.tooltipList = [];
    this.tmoUnlimitedRatePlans = false;
    this.connectByTmobile = false;
    this.unltMintPlans = false;
    this.onMonthSelection = new EventEmitter<string>();
    this.plansFiltertedByDuration = new EventEmitter<PlanOption[]>();
    this.displayConnectPlansRadioButton =
      this.featureToggleUtilService.displayConnectPlansRadioButton();
  }

  /**
   * Set default value of radio button
   */
  public ngOnInit(): void {
    switch (this.selectedFamilyId) {
      case SHARED_BROWSE_CONSTANTS.TMOBILE_PREPAID_PLAN:
        this.tmoUnlimitedRatePlans = true;
        this.connectByTmobile = false;
        this.unltMintPlans = false;
        break;
      case SHARED_BROWSE_CONSTANTS.CONNECT_BY_TMOBILE:
        this.tmoUnlimitedRatePlans = false;
        this.connectByTmobile = true;
        this.unltMintPlans = false;
        break;
      case SHARED_BROWSE_CONSTANTS.MOBILE_INTERNET_PLAN:
        this.tmoUnlimitedRatePlans = false;
        this.connectByTmobile = false;
        this.unltMintPlans = true;
        break;
      default:
        this.tmoUnlimitedRatePlans = true;
        break;
    }
    this.setDefaultMonth();
    this.handleRadioClickEvent(this.selectedDuration);
    this.showConnect = document.cookie.includes('brand=connect') ? true : false;
  }
  /**
   * Method used to destroy data
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Method handle functioanlity when month selected gets changed
   * @returns void
   */

  public handleRadioClickEvent(event: string): void {
    this.onMonthSelection.emit(event);
    this.selectedDuration = event;
    if (this.stateStoreUtilService.getDurationForSelectedConnectPlan() !== event) {
      this.stateStoreUtilService.setDurationForSelectedConnectPlan(event);
    }
    this.filterPlansByDuration();
  }

  /**
   * Method to group plans by duration
   * @returns void
   */
  private groupPlansByDurtion(plan: PlanOption[]): void {
    this.plansDuration = [];
    const groupByDuration = this.commonUtilService.groupBy(plan, 'periodLength');
    for (const [key, value] of Object.entries(groupByDuration)) {
      if (key && key !== SHARED_BROWSE_CONSTANTS.UNDEFINED) {
        const obj = { periodLength: key, periodLengthUnit: value[0].periodLengthMeasurementUnit };
        this.plansDuration.push(obj);
      }
    }
    this.defaultMonthSelected =
      this.plansByDuration && this.plansDuration.length > 1
        ? this.objectService.get(this.aemContent, 'defaultMonth', '')
        : this.objectService.get(this.aemContent, 'defaultOneMonth', '');
    this.selectedDuration =
      !!this.stateStoreUtilService.getDurationForSelectedConnectPlan() &&
      this.plansDuration.length > 1
        ? this.stateStoreUtilService.getDurationForSelectedConnectPlan()
        : this.defaultMonthSelected;
    this.filterPlansByDuration();
  }
  /**
   * Method to filter plans by duration to display
   * @returns void
   */
  private filterPlansByDuration(): void {
    if (this.plans) {
      const plansByDuration = this.plans.filter(
        (plan: PlanOption) => plan.periodLength === this.selectedDuration
      );
      this.plansByDuration =
        plansByDuration && plansByDuration.length ? plansByDuration : this.plans;
      this.plansFiltertedByDuration.emit(this.plansByDuration);
    }
  }
  /**
   * set value for selected month
   * @returns void
   */
  private setDefaultMonth(): void {
    this.selectedDuration = !!this.stateStoreUtilService.getDurationForSelectedConnectPlan()
      ? this.stateStoreUtilService.getDurationForSelectedConnectPlan()
      : this.objectService.get(this.aemContent, 'defaultMonth')
        ? this.objectService.get(this.aemContent, 'defaultMonth')
        : this.objectService.get(this.aemContent, 'defaultOneMonth');
  }
}
